import React from 'react';

import * as Sentry from '@sentry/nextjs';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { useAccountActions, useWishlistIds } from '@techstyle/react-accounts';
import { useIntl } from '@techstyle/react-intl';
import { useSessionActions, useSession } from '@techstyle/redux-core';

import {
  trackRemoveFromWishlistEvent,
  trackAddToWishlistEvent,
} from '../../actions/account';
import { toggleSignInModal } from '../../actions/auth';
import { SignUpMethod } from '../../constants/auth';
import { isProductSet } from '../../utils/selectors';
import { Component as BorderfreeGuard } from '../BorderfreeGuard';
import { Component as StaticIcon } from '../StaticIcon';

const OUTLINE_WIDTH = 2;

const Button = styled.button`
  margin: ${OUTLINE_WIDTH}px ${OUTLINE_WIDTH}px 0 auto; /* Pull right in flex layouts. */
  width: ${props => (props.overlay ? (props.large ? 58 : 40) : 34)}px;
  height: ${props => (props.overlay ? (props.large ? 58 : 40) : 34)}px;
  flex: 0 0 auto;
  background: ${props =>
    props.overlay ? 'transparent' : props.theme.colors.default};
  border-radius: 2px;
  cursor: pointer;

  > * {
    color: ${props =>
      props.overlay ? props.theme.colors.default : props.theme.colors.white};
    stroke: ${props =>
      props.overlay ? props.theme.colors.default : props.theme.colors.white};
  }

  &:focus {
    outline: ${OUTLINE_WIDTH}px solid ${props => props.theme.focusColor};
  }

  ${props =>
    props.inWishlist
      ? css`
          /* For components that want to hide the button by default - force a
             high specificity and set visible when in wishlist */
          &&& {
            visibility: visible;
          }
        `
      : ''};

  ${props =>
    props.theme.context.bca &&
    `
      width: auto;
      height: 18px;
      position: absolute;
      top: 4px;
      right: 12px;
    `};
`;

function WishlistButton({ overlay, large, product, eventCategory, ...rest }) {
  const dispatch = useDispatch();
  const accountActions = useAccountActions();
  const sessionActions = useSessionActions();
  const wishlistIds = useWishlistIds();
  const { isVisitor } = useSession();
  const iconSize = overlay && large ? 20 : 18;
  const inWishlist = wishlistIds.hasProductId(product.master_product_id);
  const intl = useIntl();

  // FIXME: There is a different endpoint for adding a set to wishlist?
  const handleWishlist = async () => {
    if (isVisitor) {
      try {
        sessionActions.updateSessionDetail({
          name: 'signup_favorites',
          value: '',
        });

        const isSet = isProductSet(product);
        const data = await dispatch(
          toggleSignInModal(
            true,
            'SIGN_UP',
            SignUpMethod.WISHLIST,
            product.master_product_id,
            isSet
          )
        );

        if (data && data.signedIn) {
          dispatch(
            trackAddToWishlistEvent({
              eventLabel: product.label,
              eventCategory: eventCategory,
            })
          );

          // This will ensure that the wishlistId will always be updated
          // before modifying and invalidating the wishlist
          await accountActions.loadWishlistIds();
          await accountActions.addWishlistItem(product.master_product_id);
        }
      } catch (err) {
        Sentry.captureException(err);
      }
    } else if (!inWishlist) {
      dispatch(
        trackAddToWishlistEvent({
          eventLabel: product.label,
          eventCategory: eventCategory,
        })
      );
      try {
        // This will ensure that the wishlistId will always be updated
        // before modifying and invalidating the wishlist
        await accountActions.loadWishlistIds();
        await accountActions.addWishlistItem(product.master_product_id);
      } catch (err) {
        Sentry.captureException(err);
      }
    } else if (inWishlist) {
      // we are tracking all the requests rather than only successful ones
      dispatch(
        trackRemoveFromWishlistEvent({
          eventLabel: product.label,
          eventCategory: eventCategory,
        })
      );
      await accountActions.deleteWishlistItem(product.master_product_id);
    }
  };

  return (
    <BorderfreeGuard>
      <Button
        overlay={overlay}
        large={large}
        inWishlist={inWishlist}
        onClick={handleWishlist}
        data-autotag={
          inWishlist ? `addtowishlist-btn-filled` : `addtowishlist-btn`
        }
        {...rest}
        aria-label={
          inWishlist
            ? intl.formatMessage({
                id: 'global_cta.wishlist_saved',
                defaultMessage: 'Remove from Favorites',
              })
            : intl.formatMessage({
                id: 'global_cta.wishlist_add',
                defaultMessage: 'Add to Favorites',
              })
        }
      >
        <StaticIcon
          type={inWishlist ? 'heartFilled' : 'heartUnfilled'}
          size={iconSize}
          strokeWidth={inWishlist ? 0 : 0.4}
        />
      </Button>
    </BorderfreeGuard>
  );
}

WishlistButton.propTypes = {
  /**
   * The elelment that triggers the addToWishlist event.
   */
  eventCategory: PropTypes.string,
  /**
   * Whether to render the small or large button version. Only applies to the
   * overlay version.
   */
  large: PropTypes.bool,
  /**
   * Whether the button is overlaying a product image or appearing as a standard
   * button.
   */
  overlay: PropTypes.bool,
  /**
   * Product to add to wishlist
   */
  product: PropTypes.object.isRequired,
};

WishlistButton.defaultProps = {
  large: false,
  overlay: false,
};

export default WishlistButton;
